import { createContext, type ComponentPropsWithoutRef, type PropsWithChildren, type ReactElement, useMemo } from 'react';
import { type LinkProps as NextLinkProps } from 'next/link';
import { type FeatureFlag, type SearchModuleProps } from '@pkgs/components/types/searchModuleTypes';
import { useAuth } from '@ev-tech/user-account-modules';
import type { TFunction } from '@ev-tech/search-modules-shared/translation';
import { SearchApi } from '@ev-tech/search-modules-api';

import { type TranslationKey } from '../types/typedTranslationKeys';
import { type PageType } from '../types/tracking';
import { DEFAULT_FEATURE_FLAGS, NEW_SEARCH_URL_MAP } from '../consts/consts';
import { DefaultImageComponent, type ImageComponent } from '../components/Image/DefaultImageComponent';

export type { TFunction } from '@ev-tech/search-modules-shared/translation';

export type ComponentTFunction = TFunction<TranslationKey>;

export type LinkComponentProps = PropsWithChildren<Omit<ComponentPropsWithoutRef<'a'>, 'href'> & NextLinkProps>;

export type LinkComponent = (props: LinkComponentProps) => JSX.Element;

export type TransComponentProps = {
  components?: Record<string, ReactElement> | ReactElement[];
  i18nKey: string;
  t: TFunction;
  values?: Record<string, unknown>;
};

export type TransComponent = (props: TransComponentProps) => JSX.Element;

export const DefaultLinkComponent: LinkComponent = ({ children, onNavigate, href, ...props }: LinkComponentProps) => {
  return (
    <a onClick={onNavigate} href={href.toString()} {...props}>
      {children}
    </a>
  );
};

export type Config = {
  mapTilerApiKey?: string;
  friendlyCaptchaSiteKey: string;
  vercelProtectionBypassToken?: string;
  searchAlertBaseUrl?: string;
};

export type TrackingConfig = {
  snowplowApplicationId?: string;
  isSnowplowInitialized?: boolean;
  pageType?: PageType;
  templateType?: PageType;
};

export type AppContextProviderType = Omit<
  SearchModuleProps,
  'translationResolver' | 'imageComponent' | 'linkComponent' | 'transComponent' | 'isMapToggledOn' | 'isAiSearchEnabled'
> & {
  ImageComponent: ImageComponent;
  t: ComponentTFunction;
  LinkComponent: LinkComponent;
  TransComponent: TransComponent;
  api: SearchApi;
  newSearchUrl: string;
  featureFlags: Record<FeatureFlag, boolean>;
};

export const AppContext = createContext<AppContextProviderType | undefined>(undefined);

export type AppContextProviderProps = PropsWithChildren<SearchModuleProps>;

export const AppContextProvider = ({
  imageComponent,
  linkComponent,
  transComponent,
  translationResolver,
  children,
  featureFlags = {},
  ...props
}: AppContextProviderProps) => {
  const { getToken } = useAuth();
  const api = useMemo(() => new SearchApi({ ...props.apiConfig, getToken }), [props.apiConfig, getToken]);

  const { searchAlertBaseUrl } = props.config;

  const newSearchUrl = searchAlertBaseUrl || NEW_SEARCH_URL_MAP[api.config.environment];

  const value = useMemo<AppContextProviderType>(() => {
    return {
      t: translationResolver,
      ImageComponent: imageComponent || DefaultImageComponent,
      LinkComponent: linkComponent || DefaultLinkComponent,
      TransComponent: transComponent,
      newSearchUrl,
      api,
      featureFlags: { ...DEFAULT_FEATURE_FLAGS, ...featureFlags },
      ...props,
    };
  }, [translationResolver, imageComponent, linkComponent, transComponent, newSearchUrl, api, featureFlags, props]);
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
